import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Eye, EyeOff, ArrowLeft } from "lucide-react";

const empty = { title: "", slug: "", excerpt: "", content_md: "", cover_media_id: null, published: false };

export default function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | post
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/blog");
      setPosts(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (p) => { setForm({ ...p }); setEditing(p); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/crm/blog", form);
        toast.success("Post created");
      } else {
        await api.patch(`/crm/blog/${editing.id}`, form);
        toast.success("Post updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/crm/blog/${p.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (editing) {
    return (
      <div data-testid="blog-editor" className="space-y-4">
        <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4"/> Back to posts
        </button>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl text-white">
            {editing === "new" ? "New Article" : `Edit: ${editing.title}`}
          </h2>
          <button onClick={save} disabled={saving} data-testid="save-post"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold hover:bg-white hover:text-black transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>

        <div className="bg-[#121216] border border-white/12 rounded-2xl p-6 space-y-4">
          <div>
            <div className="font-mono-alt text-[10px] text-white/40 mb-1">Title</div>
            <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="post-title"
              className="w-full bg-transparent text-white text-xl font-display outline-none border-b border-white/10 focus:border-[#1F5EFF] py-2"/>
          </div>
          <div>
            <div className="font-mono-alt text-[10px] text-white/40 mb-1">Slug (auto if empty)</div>
            <input value={form.slug || ""} onChange={(e) => setForm({...form, slug: e.target.value})} data-testid="post-slug"
              className="w-full bg-black/40 border border-white/10 focus:border-[#1F5EFF] rounded-xl px-4 py-2.5 outline-none text-sm text-white font-mono-alt"/>
          </div>
          <div>
            <div className="font-mono-alt text-[10px] text-white/40 mb-1">Excerpt (short summary)</div>
            <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} data-testid="post-excerpt"
              className="w-full bg-black/40 border border-white/10 focus:border-[#1F5EFF] rounded-xl px-4 py-2.5 outline-none text-sm text-white resize-none"/>
          </div>
          <div>
            <div className="font-mono-alt text-[10px] text-white/40 mb-1">Content (Markdown)</div>
            <textarea rows={18} value={form.content_md} onChange={(e) => setForm({...form, content_md: e.target.value})} data-testid="post-content"
              className="w-full bg-black/40 border border-white/10 focus:border-[#1F5EFF] rounded-xl px-4 py-3 outline-none text-sm text-white font-mono leading-relaxed"
              placeholder="# Heading&#10;&#10;Write your article here in Markdown…"/>
            <div className="mt-1 text-[10px] text-white/40">Supports # headings, **bold**, *italic*, [links](url), - lists, ![alt](image-url).</div>
          </div>
          <label className="flex items-center gap-3 pt-3 border-t border-white/12">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({...form, published: e.target.checked})}
              data-testid="post-published"/>
            <span className="text-sm text-white">
              {form.published ? "Published" : "Draft"} — {form.published ? "visible on public site" : "saved as draft only"}
            </span>
          </label>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-white">Blog</h2>
        <button onClick={startNew} data-testid="new-post-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold hover:bg-white hover:text-black transition-colors">
          <Plus className="w-4 h-4"/> New Article
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-white/40"><Loader2 className="w-4 h-4 animate-spin"/></div>
      ) : posts.length === 0 ? (
        <div className="py-16 text-center text-white/40 bg-[#121216] border border-dashed border-white/10 rounded-2xl">
          No articles yet.
        </div>
      ) : (
        <div className="bg-[#121216] border border-white/12 rounded-2xl overflow-hidden">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center gap-4 px-5 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors">
              <button onClick={() => startEdit(p)} className="flex-1 text-left min-w-0" data-testid={`post-row-${p.id}`}>
                <div className="text-white font-medium truncate">{p.title}</div>
                <div className="text-white/40 text-xs mt-0.5 font-mono-alt truncate">/{p.slug}</div>
              </button>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono-alt border ${
                p.published
                  ? "text-green-400 bg-green-400/5 border-green-400/30"
                  : "text-white/50 bg-white/5 border-white/15"
              }`}>
                {p.published ? <Eye className="w-3 h-3"/> : <EyeOff className="w-3 h-3"/>}
                {p.published ? "Published" : "Draft"}
              </span>
              <button onClick={() => remove(p)} className="p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
